import { isUnreleasedClient } from '../constants/csvMerge';

export function todayYmd() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Start date at spawn time — only open tasks get a default. */
export function spawnStartDateForStatus(status, options = {}) {
  if (status === 'open') return options.startDate || todayYmd();
  return null;
}

/** When a task becomes open, stamp start date if not already set. */
export function withStartDateWhenOpening(task, patch, options = {}) {
  if (patch?.status !== 'open' || task?.startDate) return patch;
  return { ...patch, startDate: options.startDate || todayYmd() };
}

/** AR due-for-ar keeps openForBooking / review due — not "due today". */
export function skipsAutoDueDateOnOpen(templateOrActionId) {
  return templateOrActionId === 'due-for-ar';
}

/**
 * Seed due date when a milestone/task becomes open.
 * Does not overwrite an existing due date; skips due-for-ar.
 */
export function dueDateFieldsWhenOpening(actionId, existing = {}, options = {}) {
  if (existing?.dueDate) return {};
  if (skipsAutoDueDateOnOpen(actionId)) return {};
  return { dueDate: options.dueDate || todayYmd() };
}

/** When a task becomes open, stamp due date if not already set. */
export function withDueDateWhenOpening(task, patch, options = {}) {
  if (patch?.status !== 'open') return patch;
  if (task?.dueDate || patch?.dueDate) return patch;
  if (skipsAutoDueDateOnOpen(task?.templateId)) return patch;
  return { ...patch, dueDate: options.dueDate || todayYmd() };
}

/** Start + due stamps when a task becomes open (assignee seeding stays separate). */
export function withOpenDateDefaults(task, patch, options = {}) {
  return withDueDateWhenOpening(
    task,
    withStartDateWhenOpening(task, patch, options),
    options
  );
}

export function isTaskOverdue(task) {
  if (!task || task.status === 'done' || !task.dueDate) return false;
  return task.dueDate < todayYmd();
}

export function getOverdueTaskRowClassName(task) {
  if (!isTaskOverdue(task)) return '';
  return 'bg-destructive/[0.06] hover:bg-destructive/10 dark:bg-destructive/10 dark:hover:bg-destructive/15';
}

export function getTaskStatusLabel(task) {
  if (!task) return '';
  if (task.status === 'done') return 'Done';
  if (task.status === 'skipped') return 'Skipped';
  if (task.status === 'blocked') return 'Blocked';
  if (task.status === 'pending') return 'Pending';
  if (isTaskOverdue(task)) return 'Overdue';
  return 'Open';
}

export function formatTaskDate(val) {
  if (!val) return '-';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [year, month, day] = val.split('-');
      return `${day}/${month}/${year}`;
    }
    const date = typeof val?.toDate === 'function' ? val.toDate() : new Date(val);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
}

/** Rewrites YYYY-MM-DD dates in parentheses to DD/MM/YYYY (e.g. Annual review domain titles). */
export function formatTitleDateParenthetical(title) {
  if (!title || typeof title !== 'string') return title || '';
  return title.replace(/\((\d{4}-\d{2}-\d{2})\)/g, (_, ymd) => `(${formatTaskDate(ymd)})`);
}

/** DD/MM/YYYY h:mm am/pm for task notes and timestamps. */
export function formatTaskDateTime(val) {
  if (!val) return '-';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return formatTaskDate(val);
    }
    const date = typeof val?.toDate === 'function' ? val.toDate() : new Date(val);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day}/${month}/${year} ${time}`;
  } catch {
    return '-';
  }
}

const TASK_NOTE_TITLE_SUFFIX = /\s*—\s*Task Note\s*$/i;

/** Task name for note metadata — prefers live title, else strips the file-note suffix. */
export function resolveTaskNoteTitle(note, fallbackTitle = '') {
  const fromFallback = String(fallbackTitle || '').trim();
  if (fromFallback) return fromFallback;
  return String(note?.title || '').replace(TASK_NOTE_TITLE_SUFFIX, '').trim();
}

/** Note list caption parts: title vs team · date/time · author. */
export function getFileNoteMetaParts(note, fallbackTitle = '') {
  const title = resolveTaskNoteTitle(note, fallbackTitle)
    || String(note?.category || '').trim()
    || '';
  const metaParts = [];
  const team = String(note?.team || '').trim();
  if (team) metaParts.push(team);
  if (note?.createdAt) metaParts.push(formatTaskDateTime(note.createdAt));
  const author = String(note?.createdByName || note?.createdBy || '').trim();
  if (author) metaParts.push(author);
  return { title, meta: metaParts.join(' · ') };
}

/** Note list caption: label · team · date/time · author. */
export function formatFileNoteMetaLine(note, fallbackTitle = '') {
  const { title, meta } = getFileNoteMetaParts(note, fallbackTitle);
  return [title, meta].filter(Boolean).join(' · ');
}

/** @deprecated Use formatFileNoteMetaLine — kept for existing imports. */
export function formatTaskNoteMetaLine(note, fallbackTitle = '') {
  return formatFileNoteMetaLine(note, fallbackTitle);
}

export function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').trim();
}

export function previewNotes(notes, maxLen = 80) {
  const s = stripHtml(notes);
  if (!s) return '';
  return s.length > maxLen ? s.slice(0, maxLen) + '...' : s;
}

export function getClientDisplayName(client) {
  if (!client) return '';
  const personal = client.personal || {};
  const surname = personal.surname || personal.lastName;
  if (personal.firstName || surname) {
    return `${personal.firstName || ''} ${surname || ''}`.trim();
  }
  if (personal.fullName) return personal.fullName;
  return personal.preferredName || personal.email || client.name || client.id || '';
}

/** Clients whose open tasks are hidden from assigned-task views (read-side; matches DNQ pattern). */
export function isTaskClientExcluded(client) {
  return client?.isDnq === true || client?.isArchived === true || isUnreleasedClient(client);
}

export function buildExcludedTaskClientIdSet(clients) {
  return new Set(
    (clients || []).filter((c) => isTaskClientExcluded(c) && c?.id).map((c) => c.id)
  );
}
